import {useDispatch} from 'react-redux';
import {useMemo, useState} from 'react';

import {head, isNonEmpty} from '@famly/stat_ts-utils_non-empty-array';
import {
    transformApiBillPayer,
    transformApiChildData,
    transformApiPlan,
} from 'web-app/react/routes/account/institution/finance/power-page/transformers';
import {useFinanceOverviewChildren} from 'web-app/react/simplified-us-billing-plan/hooks/use-finance-overview-children';
import {useSimplifiedUSBillingPlanModal} from 'web-app/react/simplified-us-billing-plan/hooks/use-simplified-us-billing-plan-modal';
import {usePlanTemplatesModal} from 'web-app/react/simplified-us-billing-plan/templates/use-plan-templates-modal';

import {fetchChildPlansData} from './actions';
import {useCanAccessUSPlans} from './use-can-access-us-plans';
import {useUSPlansTracking} from './use-us-plans-tracking';

export const useManageUSPlans = (siteId: string, childId: string) => {
    const {children, isChildrenLoading, refetch} = useFinanceOverviewChildren(siteId, [childId]);
    const [delayedRefetching, setDelayedRefetching] = useState(false);
    // A refetch that takes replica lag into account.
    const delayedRefetch = async () => {
        setDelayedRefetching(true);
        await wait(3000);
        reduxDispatch(fetchChildPlansData.action(siteId, firstChild?.child.id));
        await refetch();
        setDelayedRefetching(false);
    };

    const reduxDispatch = useDispatch();

    const firstChild = useMemo(() => (isNonEmpty(children) ? head(children) : null), [children]);
    const powerPageChild = useMemo(
        () =>
            firstChild
                ? {
                      child: transformApiChildData(firstChild.child),
                      billPayers: firstChild.billPayers.map(transformApiBillPayer),
                      plans: firstChild.plans.map(transformApiPlan),
                      nextInvoice: null,
                  }
                : null,
        [firstChild],
    );

    const simplifiedUSBillingPlanModal = useSimplifiedUSBillingPlanModal({
        siteId,
        allChildren: powerPageChild ? [powerPageChild] : [],
        afterSubmission: () => {
            delayedRefetch();
        },
    });

    const simplifiedUSBillingPlanWithPlanTemplatesModal = usePlanTemplatesModal({
        selectedChildIds: [childId],
        simplifiedUSBillingPlanModalOpen:
            !simplifiedUSBillingPlanModal.isLoading && !isChildrenLoading ? simplifiedUSBillingPlanModal.open : null,
        siteId,
    });

    const {canAccessUSPlans, isCanAccessUSPlansLoading} = useCanAccessUSPlans(siteId);

    const {trackUSPlans} = useUSPlansTracking(siteId);

    return {
        simplifiedUSBillingPlanWithPlanTemplatesModal,
        simplifiedUSBillingPlanModal,
        canAccessUSPlans,
        isCanAccessUSPlansLoading,
        isChildrenLoading: isChildrenLoading || delayedRefetching,
        trackUSPlans,
        refetchChildren: refetch,
        delayedRefetchChildren: delayedRefetch,
    };
};

const wait = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));
